import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';
import { useAdmin } from '../../hooks/useAdmin';

interface PushNotificationData {
  title: string;
  body: string;
  emails: string[];
}

interface NotificationDraft {
  id?: string;
  title: string;
  body: string;
  emails: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  id: number;
  email: string;
  username: string;
  userType: string;
  name?: string;
}

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  body: string;
}

interface NotificationHistory {
  timestamp: string;
  title: string;
  body: string;
  recipients: string[];
  success: number;
  failure: number;
}

const PREDEFINED_TEMPLATES: NotificationTemplate[] = [
  {
    id: 'welcome',
    name: 'Welcome Message',
    title: 'Welcome to Chime!',
    body: 'Thank you for joining Chime. We\'re excited to have you in our community.'
  },
  {
    id: 'maintenance',
    name: 'Maintenance Notice',
    title: 'Scheduled Maintenance',
    body: 'Chime will undergo scheduled maintenance on [DATE]. Please save your work.'
  },
  {
    id: 'update',
    name: 'App Update',
    title: 'New Update Available',
    body: 'A new version of Chime is available. Please update to get the latest features and improvements.'
  },
  {
    id: 'announcement',
    name: 'General Announcement',
    title: 'Important Announcement',
    body: '[Your announcement here]'
  }
];

export default function PushNotifications() {
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [lastResult, setLastResult] = useState<any>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);

  const { showSuccess, showError } = useNotifications();
  const { requireAdmin } = useAdmin();

  // Require admin access
  requireAdmin();

  // Fetch all users for search
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      try {
        return await apiService.request<User[]>('/api/v1/admin/users');
      } catch (err) {
        console.error('Failed to fetch users:', err);
        return [];
      }
    }
  });

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch notification drafts
  const { data: drafts = [], refetch: refetchDrafts } = useQuery<NotificationDraft[]>({
    queryKey: ['notification-drafts'],
    queryFn: async () => {
      try {
        return await apiService.request<NotificationDraft[]>('/api/v1/admin/notification_drafts');
      } catch (err) {
        console.error('Failed to fetch drafts:', err);
        return [];
      }
    }
  });

  const sendNotificationMutation = useMutation({
    mutationFn: async (data: PushNotificationData) => {
      try {
        return await apiService.request<any>('/api/v1/admin/push_notification', {
          method: 'POST',
          body: JSON.stringify(data),
        });
      } catch (err: any) {
        if (err.message?.includes('404') || err.message?.includes('Cannot POST')) {
          throw new Error('Push notification endpoint not implemented yet');
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      // Handle immediate notification
      const result = data?.result?.[0];
      setLastResult(result);
      setShowResultModal(true);

      if (result) {
        const successCount = result.success || 0;
        const failureCount = result.failure || 0;
        const emailResults = result.emailResults || [];
        const emailsWithoutDevices = result.emailsWithoutDevices || [];

        // Build detailed message
        let detailsMessage = '';

        if (emailResults.length > 0) {
          const successEmails = emailResults.filter((r: any) => r.success).map((r: any) => r.email);
          const failedEmails = emailResults.filter((r: any) => !r.success).map((r: any) => `${r.email} (${r.error})`);

          if (successEmails.length > 0) {
            detailsMessage += `\n✅ Delivered to: ${successEmails.join(', ')}`;
          }
          if (failedEmails.length > 0) {
            detailsMessage += `\n❌ Failed: ${failedEmails.join(', ')}`;
          }
        }

        if (emailsWithoutDevices.length > 0) {
          detailsMessage += `\n⚠️ No registered devices: ${emailsWithoutDevices.join(', ')}`;
        }

        // Save to history
        const historyItem: NotificationHistory = {
          timestamp: new Date().toISOString(),
          title: notificationTitle,
          body: notificationBody,
          recipients: selectedEmails,
          success: successCount,
          failure: failureCount
        };
        const history = JSON.parse(localStorage.getItem('notificationHistory') || '[]');
        history.unshift(historyItem);
        localStorage.setItem('notificationHistory', JSON.stringify(history.slice(0, 20)));

        // Show appropriate notification
        if (failureCount === 0 && emailsWithoutDevices.length === 0) {
          showSuccess('Notification Sent!', `Delivered successfully to ${successCount} device(s)`);
        } else if (successCount === 0) {
          showError('Delivery Failed', `Failed to deliver notification. Check the details.`);
        } else {
          showError('Partial Success', `Delivered to ${successCount}, failed for ${failureCount}`);
        }
      } else {
        showSuccess('Notification Sent!', 'Push notification sent successfully');
      }

      // Reset form
      setNotificationTitle('');
      setNotificationBody('');
      setSelectedEmails([]);
      setEmailInput('');
      setEditingDraftId(null);
    },
    onError: (error: Error) => {
      console.error('Notification error:', error);

      if (error.message.includes('endpoint not implemented')) {
        showError('Feature Not Available', 'Push notifications endpoint has not been implemented on the backend yet.');
      } else if (error.message.includes('401')) {
        showError('Authentication Error', 'Your session may have expired. Please refresh and try again.');
      } else if (error.message.includes('403')) {
        showError('Authorization Error', 'You do not have permission to send notifications.');
      } else if (error.message.includes('400')) {
        showError('Bad Request', 'Please check your input and try again.');
      } else {
        showError('Send Failed', 'Could not send notification. Please try again.');
      }
    },
  });

  // Draft mutations
  const saveDraftMutation = useMutation({
    mutationFn: async (draft: NotificationDraft) => {
      console.log('🔵 Saving draft...', draft);
      return await apiService.request('/api/v1/admin/notification_drafts', {
        method: 'POST',
        body: JSON.stringify(draft)
      });
    },
    onSuccess: (data) => {
      console.log('✅ Draft saved successfully:', data);
      console.log('🔔 Calling showSuccess...');
      showSuccess('Draft Saved', 'Notification draft saved successfully');
      refetchDrafts();
    },
    onError: (error: Error) => {
      console.error('❌ Save draft error:', error);
      showError('Save Failed', 'Could not save draft. Please try again.');
    }
  });

  const deleteDraftMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🔵 Deleting draft...', id);
      return await apiService.request(`/api/v1/admin/notification_drafts/${id}`, {
        method: 'DELETE'
      });
    },
    onSuccess: (data) => {
      console.log('✅ Draft deleted successfully:', data);
      console.log('🔔 Calling showSuccess...');
      showSuccess('Draft Deleted', 'Notification draft deleted successfully');
      refetchDrafts();
    },
    onError: (error: Error) => {
      console.error('❌ Delete draft error:', error);
      showError('Delete Failed', 'Could not delete draft. Please try again.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!notificationTitle.trim() || !notificationBody.trim() || selectedEmails.length === 0) {
      showError('Missing Information', 'Please fill in all fields and add at least one email recipient.');
      return;
    }

    // Validate emails
    const invalidEmails = selectedEmails.filter(email => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      showError('Invalid Emails', `Please fix invalid emails: ${invalidEmails.join(', ')}`);
      return;
    }

    const payload: PushNotificationData = {
      title: notificationTitle.trim(),
      body: notificationBody.trim(),
      emails: selectedEmails,
    };

    sendNotificationMutation.mutate(payload);
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailInput(value);

    // Sync search query with input for live filtering
    setSearchQuery(value);

    const emailList = value
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    setSelectedEmails(emailList);
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addEmail = (email: string) => {
    // Filter out any partial/invalid emails before adding the selected one
    const validEmails = selectedEmails.filter(e => isValidEmail(e));

    if (!validEmails.includes(email)) {
      const newEmails = [...validEmails, email];
      setSelectedEmails(newEmails);
      setEmailInput(newEmails.join(', '));
    } else {
      // Email already exists, just clean up the input
      setEmailInput(validEmails.join(', '));
    }

    setSearchQuery('');
    setShowUserSearch(false);
  };

  const removeEmail = (emailToRemove: string) => {
    const newEmails = selectedEmails.filter(e => e !== emailToRemove);
    setSelectedEmails(newEmails);
    setEmailInput(newEmails.join(', '));
  };

  const addAllByType = (userType: string) => {
    const emailsToAdd = users
      .filter(u => userType === 'all' || u.userType === userType)
      .map(u => u.email);
    const uniqueEmails = Array.from(new Set([...selectedEmails, ...emailsToAdd]));
    setSelectedEmails(uniqueEmails);
    setEmailInput(uniqueEmails.join(', '));
  };

  const applyTemplate = (templateId: string) => {
    const template = PREDEFINED_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setNotificationTitle(template.title);
      setNotificationBody(template.body);
      setSelectedTemplate(templateId);
    }
    setShowTemplates(false);
  };

  // Draft handlers
  const handleSaveDraft = () => {
    console.log('📝 handleSaveDraft called');
    if (!notificationTitle.trim() || !notificationBody.trim()) {
      showError('Missing Information', 'Please fill in title and body to save a draft.');
      return;
    }

    const draft: NotificationDraft = {
      id: editingDraftId || undefined,
      title: notificationTitle,
      body: notificationBody,
      emails: selectedEmails
    };
    console.log('📤 Calling saveDraftMutation.mutate with:', draft);
    saveDraftMutation.mutate(draft);
  };

  const handleLoadDraft = (draft: NotificationDraft) => {
    setNotificationTitle(draft.title);
    setNotificationBody(draft.body);
    setSelectedEmails(draft.emails);
    setEmailInput(draft.emails.join(', '));
    setEditingDraftId(draft.id || null);
    setShowDrafts(false);
    showSuccess('Draft Loaded', 'Notification draft loaded successfully');
  };

  const handleDeleteDraft = (id: string) => {
    setDraftToDelete(id);
    setShowDeleteDraftConfirm(true);
  };

  const confirmDeleteDraft = () => {
    if (draftToDelete) {
      deleteDraftMutation.mutate(draftToDelete);
      if (editingDraftId === draftToDelete) {
        setEditingDraftId(null);
      }
    }
    setShowDeleteDraftConfirm(false);
    setDraftToDelete(null);
  };

  const cancelDeleteDraft = () => {
    setShowDeleteDraftConfirm(false);
    setDraftToDelete(null);
  };

  const handleNewNotification = () => {
    setNotificationTitle('');
    setNotificationBody('');
    setSelectedEmails([]);
    setEmailInput('');
    setEditingDraftId(null);
    setSelectedTemplate('');
  };

  const notificationHistory: NotificationHistory[] = JSON.parse(
    localStorage.getItem('notificationHistory') || '[]'
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="theme-card shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM15 17H9a2 2 0 01-2-2V5a2 2 0 012-2h6a2 2 0 012 2v10a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold theme-text">Send Push Notification</h1>
                <p className="text-sm theme-text-muted">Send notifications to specific users or groups</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDrafts(true)}
                className="px-4 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Drafts ({drafts.length})</span>
              </button>
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                className="px-4 py-2 text-sm bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
              >
                📝 Templates
              </button>
              <button
                type="button"
                onClick={() => setShowHistory(!showHistory)}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                📜 History
              </button>
            </div>
          </div>
        </div>

        {/* Templates Panel */}
        {showTemplates && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20">
            <h3 className="text-lg font-semibold theme-text mb-3">📝 Notification Templates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PREDEFINED_TEMPLATES.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => applyTemplate(template.id)}
                  className={`p-4 text-left rounded-lg border-2 transition-all ${
                    selectedTemplate === template.id
                      ? 'border-purple-500 bg-purple-100 dark:bg-purple-900'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                  }`}
                >
                  <div className="font-semibold theme-text">{template.name}</div>
                  <div className="text-sm theme-text-muted mt-1">{template.title}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* History Panel */}
        {showHistory && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-lg font-semibold theme-text mb-3">📜 Notification History</h3>
            {notificationHistory.length === 0 ? (
              <p className="text-sm theme-text-muted">No notifications sent yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {notificationHistory.map((item, index) => (
                  <div key={index} className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold theme-text text-sm">{item.title}</div>
                        <div className="text-xs theme-text-muted mt-1">{item.body.substring(0, 100)}...</div>
                        <div className="text-xs theme-text-muted mt-2">
                          To: {item.recipients.slice(0, 3).join(', ')}
                          {item.recipients.length > 3 && ` +${item.recipients.length - 3} more`}
                        </div>
                      </div>
                      <div className="text-xs ml-4">
                        <div className="text-green-600 dark:text-green-400">✅ {item.success}</div>
                        {item.failure > 0 && <div className="text-red-600 dark:text-red-400">❌ {item.failure}</div>}
                        <div className="theme-text-muted mt-1">{new Date(item.timestamp).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Quick Actions */}
          <div>
            <label className="block text-sm font-medium theme-text mb-2">
              👥 Quick Actions - Send to:
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => addAllByType('surgeon')}
                className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              >
                🩺 All Surgeons
              </button>
              <button
                type="button"
                onClick={() => addAllByType('staff')}
                className="px-4 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
              >
                👨‍⚕️ All Staff
              </button>
              <button
                type="button"
                onClick={() => addAllByType('rep')}
                className="px-4 py-2 text-sm bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
              >
                👔 All Reps
              </button>
              <button
                type="button"
                onClick={() => addAllByType('all')}
                className="px-4 py-2 text-sm bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
              >
                🌐 Everyone
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="notificationTitle" className="block text-sm font-medium theme-text mb-2">
              📢 Notification Title
            </label>
            <input
              type="text"
              id="notificationTitle"
              className="w-full theme-input px-3 py-2 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter notification title..."
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              required
            />
          </div>

          {/* Body */}
          <div>
            <label htmlFor="notificationBody" className="block text-sm font-medium theme-text mb-2">
              💬 Notification Body
            </label>
            <textarea
              id="notificationBody"
              className="w-full theme-input px-3 py-2 rounded-md resize-none focus:ring-primary-500 focus:border-primary-500"
              rows={4}
              placeholder="Enter your notification message..."
              value={notificationBody}
              onChange={(e) => setNotificationBody(e.target.value)}
              required
            />
            <div className="text-xs theme-text-muted mt-1">
              {notificationBody.length} characters
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label htmlFor="emails" className="block text-sm font-medium theme-text mb-2">
              📧 Email Recipients
            </label>
            <div className="relative">
              <input
                type="text"
                id="emails"
                className="w-full theme-input px-3 py-2 pr-24 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Type to search users or enter emails separated by commas..."
                value={emailInput}
                onChange={(e) => {
                  handleEmailInputChange(e);
                  setSearchQuery(e.target.value);
                  setShowUserSearch(true);
                }}
                onFocus={() => {
                  setShowUserSearch(true);
                  setSearchQuery(emailInput);
                }}
                onBlur={() => {
                  // Delay hiding to allow clicking on search results
                  setTimeout(() => setShowUserSearch(false), 200);
                }}
                required
              />
              <div className="absolute right-2 top-2 text-xs theme-text-muted pointer-events-none">
                {selectedEmails.length} selected
              </div>
            </div>
            <p className="text-xs theme-text-muted mt-1">
              Start typing to search users, or enter email addresses separated by commas
            </p>

            {/* User Search Dropdown */}
            {showUserSearch && emailInput.trim().length > 0 && (
              <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 max-h-64 overflow-y-auto shadow-lg">
                {filteredUsers.length === 0 ? (
                  <p className="text-sm theme-text-muted text-center py-4">No users found matching "{emailInput}"</p>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsers.slice(0, 10).map(user => {
                      const isSelected = selectedEmails.includes(user.email);
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => addEmail(user.email)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                            isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm theme-text font-medium">{user.name || user.username}</div>
                              <div className="text-xs theme-text-muted">{user.email}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded ${
                                user.userType === 'surgeon' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :
                                user.userType === 'staff' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                                'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                              }`}>
                                {user.userType}
                              </span>
                              {isSelected && (
                                <span className="text-primary-500">✓</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {filteredUsers.length > 10 && (
                      <div className="px-4 py-2 text-xs theme-text-muted text-center bg-gray-50 dark:bg-gray-800">
                        Showing 10 of {filteredUsers.length} users. Type more to narrow results.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected Emails */}
            {selectedEmails.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedEmails.map((email, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1 rounded-full text-xs flex items-center gap-2 ${
                      isValidEmail(email)
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                    }`}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      className="hover:text-red-600 dark:hover:text-red-400"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Drafts feature - to be fully implemented later */}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 theme-text rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                👁️ {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
            <div className="flex items-center space-x-3">
              {editingDraftId && (
                <button
                  type="button"
                  onClick={handleNewNotification}
                  className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 theme-text rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  📝 New
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={!notificationTitle.trim() || !notificationBody.trim()}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  !notificationTitle.trim() || !notificationBody.trim()
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                }`}
              >
                💾 Save Draft
              </button>
              <button
                type="submit"
              disabled={sendNotificationMutation.isPending || !notificationTitle.trim() || !notificationBody.trim() || selectedEmails.length === 0}
              className={`px-6 py-2 text-white text-sm font-medium rounded-md transition-all flex items-center space-x-2 ${
                sendNotificationMutation.isPending || !notificationTitle.trim() || !notificationBody.trim() || selectedEmails.length === 0
                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
                  : 'bg-primary-600 hover:bg-primary-700 hover:shadow-lg cursor-pointer'
              }`}
            >
              {sendNotificationMutation.isPending ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Send Notification</span>
                </>
              )}
            </button>
            </div>
          </div>
        </form>

        {/* Preview Panel */}
        {showPreview && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-lg font-semibold theme-text mb-3">👁️ Notification Preview</h3>
            <div className="max-w-sm mx-auto">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM15 17H9a2 2 0 01-2-2V5a2 2 0 012-2h6a2 2 0 012 2v10a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold theme-text text-sm">
                      {notificationTitle || 'Notification Title'}
                    </div>
                    <div className="text-sm theme-text-muted mt-1">
                      {notificationBody || 'Notification message will appear here...'}
                    </div>
                    <div className="text-xs theme-text-muted mt-2">
                      Now
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Result Modal */}
      {showResultModal && lastResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold theme-text">📊 Delivery Results</h2>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{lastResult.success || 0}</div>
                  <div className="text-sm text-green-700 dark:text-green-300">Successful Deliveries</div>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{lastResult.failure || 0}</div>
                  <div className="text-sm text-red-700 dark:text-red-300">Failed Deliveries</div>
                </div>
              </div>

              {lastResult.emailResults && lastResult.emailResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold theme-text mb-2">Detailed Results:</h3>
                  {(() => {
                    // Deduplicate by email - show each email once
                    // Success if ANY device succeeded for that email
                    const emailMap = new Map();
                    lastResult.emailResults.forEach((result: any) => {
                      const existing = emailMap.get(result.email);
                      if (!existing || result.success) {
                        emailMap.set(result.email, result);
                      }
                    });
                    return Array.from(emailMap.values()).map((result: any, index: number) => (
                      <div key={index} className={`p-3 rounded-lg border ${
                        result.success
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>{result.success ? '✅' : '❌'}</span>
                            <span className="text-sm font-medium theme-text">{result.email}</span>
                          </div>
                          {result.error && (
                            <span className="text-xs text-red-600 dark:text-red-400">{result.error}</span>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}

              {lastResult.emailsWithoutDevices && lastResult.emailsWithoutDevices.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">⚠️ No Registered Devices:</h3>
                  <div className="text-sm text-yellow-700 dark:text-yellow-400">
                    {lastResult.emailsWithoutDevices.join(', ')}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowResultModal(false)}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drafts Modal */}
      {showDrafts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold theme-text">📋 Manage Notification Drafts</h3>
                <button
                  onClick={() => setShowDrafts(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {drafts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📝</div>
                  <p className="text-lg theme-text-secondary">No drafts saved yet</p>
                  <p className="text-sm theme-text-muted mt-2">Save notification drafts to quickly reuse them later</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className={`border rounded-lg p-4 transition-all ${
                        editingDraftId === draft.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium theme-text text-lg mb-1">
                            {draft.title}
                            {editingDraftId === draft.id && (
                              <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                                Currently Editing
                              </span>
                            )}
                          </h4>
                          <p className="text-sm theme-text-secondary line-clamp-2 mb-2">
                            {draft.body}
                          </p>
                          <div className="flex items-center gap-4 text-xs theme-text-tertiary">
                            <span>📧 {draft.emails.length} recipient{draft.emails.length !== 1 ? 's' : ''}</span>
                            <span>•</span>
                            <span>🕒 {new Date(draft.updatedAt || draft.createdAt || '').toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleLoadDraft(draft)}
                            className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors whitespace-nowrap"
                          >
                            📝 Load
                          </button>
                          <button
                            onClick={() => handleDeleteDraft(draft.id!)}
                            className="px-4 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors whitespace-nowrap"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowDrafts(false)}
                className="px-6 py-2 bg-gray-100 dark:bg-gray-700 theme-text rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Draft Confirmation Modal */}
      {showDeleteDraftConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold theme-text mb-4">Delete Draft</h3>
            <p className="theme-text-secondary mb-6">Are you sure you want to delete this draft?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDeleteDraft}
                className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 theme-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDraft}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
